import { ClickAwayListener, Popper } from "@mui/material";
import { useHandler } from "@redotech/react-util/hook";
import CalendarIcon from "@redotech/redo-icons/calendar-date.svg";
import { getDateString } from "@redotech/util/date-format";
import classNames from "classnames";
import { memo, useMemo, useState } from "react";
import { Flex } from "../../flex";
import { RedoButton, RedoButtonSize } from "../buttons/redo-button";
import { DatePicker } from "./date-picker/date-picker-menu";
import * as styles from "./redo-single-date-picker.module.css";

export interface RedoSingleDatePickerProps {
  date: Temporal.PlainDate | undefined;
  disabled?: boolean;
  setDate: (date: Temporal.PlainDate) => void;
  buttonSize?: RedoButtonSize;
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  allowNoDateOption?: boolean;
  dateStringOptions?: Pick<
    Intl.DateTimeFormatOptions,
    "year" | "month" | "day"
  >;
  /** Show Active/Expired badge based on whether date is in future or past */
  showStatus?: boolean;
}

export const RedoSingleDatePicker = memo(function RedoSingleDatePicker({
  date,
  disabled,
  setDate,
  minDate,
  maxDate,
  buttonSize = "lg",
  allowNoDateOption = false,
  dateStringOptions = {},
  showStatus = false,
}: RedoSingleDatePickerProps) {
  const buttonDisplay = date
    ? getDateString(date, dateStringOptions)
    : "Select date";

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const [pickerOpen, setPickerOpen] = useState(false);

  const status = useMemo(() => {
    if (!showStatus || !date) {
      return null;
    }
    const today = Temporal.Now.plainDateISO();
    return Temporal.PlainDate.compare(date, today) > 0 ? "active" : "expired";
  }, [showStatus, date]);

  const handleSetCustomDate = useHandler((value: Temporal.PlainDate | null) => {
    if (!value) {
      return;
    }
    setDate(value);
    setPickerOpen(false);
  });

  const handleClick = useHandler((e: React.MouseEvent) => {
    e.stopPropagation();
    setPickerOpen((open) => !open);
  });

  const statusChip = status && (
    <div
      className={classNames(styles.statusChip, {
        [styles.active]: status === "active",
        [styles.expired]: status === "expired",
      })}
    >
      {status === "active" ? "Active" : "Expired"}
    </div>
  );

  return (
    <>
      <Flex
        align="center"
        className={classNames(styles.container, { [styles.hasStatus]: status })}
        gap="none"
      >
        {statusChip}
        <RedoButton
          centerItems={false}
          disabled={disabled}
          hierarchy="secondary"
          IconLeading={CalendarIcon}
          onClick={handleClick}
          ref={setDropdownAnchor}
          size={buttonSize}
          text={buttonDisplay}
        />
      </Flex>
      <ClickAwayListener onClickAway={() => setPickerOpen(false)}>
        <Popper
          anchorEl={dropdownAnchor}
          modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
          open={pickerOpen}
        >
          <Flex>
            <DatePicker
              allowNoDateOption={allowNoDateOption}
              maxDate={maxDate}
              minDate={minDate}
              onDateChange={handleSetCustomDate}
              selectedDate={date || null}
            />
          </Flex>
        </Popper>
      </ClickAwayListener>
    </>
  );
});
